import { create } from 'zustand';
import type { Platform, ToneType, UnifiedImage, AIGeneratedImage, AICarouselData, DeterministicScore, Fabrication, PostWarning, GenerationFuel } from '@/types';

// ── Module-level stream singleton (not Zustand state — no reactivity needed) ──
let _activeStream: { abort: () => void } | null = null;
export const setActiveStream = (s: { abort: () => void } | null) => { _activeStream = s; };
export const abortActiveStream = () => { _activeStream?.abort(); _activeStream = null; };
export const getActiveStream = () => _activeStream;

// Clean up old persisted data (one-time migration)
if (typeof window !== 'undefined') {
  try {
    localStorage.removeItem('zupa-posts-storage');
  } catch {
    // Ignore errors (e.g., localStorage not available)
  }
}

// Image mode options
export type ImageMode = 'none' | 'custom' | 'suggestions' | 'ai_generate';

// Carousel mode options — separate from image mode (mutually exclusive)
export type CarouselMode = 'none' | 'custom_carousel' | 'ai_carousel';

interface GeneratedPlatformData {
  variants: string[];
  hashtags: string[][];
  carouselSlides?: string[][];
  tweets?: string[][];
  hookTypes: string[];
  principlesUsed: string[];
  explanation: string;
  recommendedImageIndex: number;
  deterministicScores?: (DeterministicScore | null)[];
  // Quality metrics per variant (for persistence on save)
  boldCounts?: (number | null)[];
  humanizerUsed?: (boolean | null)[];
  regenCounts?: (number | null)[];
  // Psychology principle metadata per variant
  principleLabels?: (string | null)[];
  whyLines?: (string | null)[];
  principleIds?: (string | null)[];
  fabrications?: (Fabrication | null)[];
  warnings?: (PostWarning[] | null)[];
  // Sprint 25 ("The Hungry Bank") — per-variant lineage back to the bank, and the
  // whole post's fuel readout (same for every variant of one platform's generation).
  bankItemIds?: (string[] | null)[];
  fuel?: GenerationFuel | null;
}

export interface GenerationHistoryItem {
  id: string;
  prompt: string;
  platforms: Platform[];
  generatedData: Record<Platform, GeneratedPlatformData>;
  selectedPostIndex: Record<Platform, number>;
  unifiedImages: UnifiedImage[];
  aiGeneratedImages: Record<Platform, AIGeneratedImage | null>;
  aiCarouselData: Record<Platform, AICarouselData | null>;
  sharedPrinciples: string[];
  sharedExplanation: string;
  psychologySelection: import('@/types').PrincipleSelectionEvent | null;
  timestamp: number;
}

// Track saved posts with their database IDs and favorite status
interface SavedPostInfo {
  dbId: string;
  isFavorited: boolean;
}

// Generation timing stats
interface GenerationTiming {
  totalTimeMs: number;
  analysisTimeMs: number;
  platformTimesMs: Record<string, number>;
  platformsCount: number;
  variationsCount: number;
  timeSavedMinutes: number;
}

// Workspace-specific data that gets stored per workspace
interface WorkspacePostData {
  prompt: string;
  selectedServices: string[];
  selectedPlatforms: Platform[];
  platformTones: Record<Platform, ToneType | null>;
  generatedData: Record<Platform, GeneratedPlatformData>;
  selectedPostIndex: Record<Platform, number>;
  sharedPrinciples: string[];
  sharedExplanation: string;
  psychologySelection: import('@/types').PrincipleSelectionEvent | null;
  savedPosts: Record<string, SavedPostInfo>;
  unifiedImages: UnifiedImage[];
  platformImageRecommendations: Record<Platform, number>;
  customImages: Record<Platform, string | null>;
  aiGeneratedImages: Record<Platform, AIGeneratedImage | null>;
  aiCarouselData: Record<Platform, AICarouselData | null>;
  generationTiming: GenerationTiming | null;
  generationHistory: GenerationHistoryItem[];
  currentSessionId: string | null;  // Active history session for threading
  lastAccessedAt: number;  // For LRU eviction
}

interface PostsState {
  // Global state (not workspace-specific)
  currentWorkspaceId: string | null;
  imageMode: ImageMode;  // User preference, global across workspaces
  carouselMode: CarouselMode;  // Carousel preference, mutually exclusive with imageMode
  wantsTextCarousel: boolean;  // Sprint 15: IG text carousel (caption + slides) vs caption-only; independent of imageMode/carouselMode
  threadMode: boolean;  // Sprint 16: X thread mode (3-5 tweets) vs single post; independent of imageMode/carouselMode

  // Transient state (not persisted)
  isGenerating: boolean;
  aiImageLoading: Record<Platform, boolean>;
  aiImageError: Record<Platform, string | null>;

  // Workspace-specific data
  workspaceData: Record<string, WorkspacePostData>;

  // Cached workspace data - stable reference for current workspace
  // This prevents infinite re-renders by avoiding new object creation on every selector call
  _cachedWorkspaceData: WorkspacePostData;

  // Workspace switching
  setCurrentWorkspace: (workspaceId: string | null) => void;

  // Form state actions (operate on current workspace)
  setPrompt: (prompt: string) => void;
  setSelectedServices: (services: string[]) => void;
  setImageMode: (mode: ImageMode) => void;
  setCarouselMode: (mode: CarouselMode) => void;
  setWantsTextCarousel: (v: boolean) => void;
  setThreadMode: (v: boolean) => void;
  setSelectedPlatforms: (platforms: Platform[]) => void;
  togglePlatform: (platform: Platform) => void;
  setPlatformTone: (platform: Platform, tone: ToneType | null) => void;
  setGeneratedData: (data: Record<Platform, GeneratedPlatformData>) => void;
  updatePlatformData: (platform: Platform, data: GeneratedPlatformData) => void;
  setSelectedPostIndex: (platform: Platform, index: number) => void;

  // Shared principles and explanation
  setSharedPrinciples: (principles: string[]) => void;
  setSharedExplanation: (explanation: string) => void;
  setPsychologySelection: (selection: import('@/types').PrincipleSelectionEvent | null) => void;

  // Saved posts management
  setSavedPost: (platform: Platform, variantIndex: number, dbId: string, isFavorited: boolean) => void;
  updateSavedPostFavorite: (platform: Platform, variantIndex: number, isFavorited: boolean) => void;
  getSavedPost: (platform: Platform, variantIndex: number) => SavedPostInfo | undefined;

  setGenerationTiming: (timing: GenerationTiming | null) => void;
  setUnifiedImages: (images: UnifiedImage[]) => void;
  setPlatformImageRecommendations: (recs: Record<Platform, number>) => void;
  setCustomImage: (platform: Platform, image: string | null) => void;

  // AI Image actions
  setAIGeneratedImage: (platform: Platform, image: AIGeneratedImage | null) => void;
  setTextOverlay: (platform: Platform, overlay: AIGeneratedImage['textOverlay']) => void;
  setAIImageLoading: (platform: Platform, loading: boolean) => void;
  setAIImageError: (platform: Platform, error: string | null) => void;
  clearAIImages: () => void;

  // Carousel actions
  setAICarouselData: (platform: Platform, data: AICarouselData | null) => void;
  updateCarouselSlide: (platform: Platform, slideIndex: number, slide: Partial<import('@/types').AICarouselSlide>) => void;
  setCarouselActiveSlide: (platform: Platform, index: number) => void;
  setCarouselSlideTextOverlay: (platform: Platform, slideIndex: number, overlay: AIGeneratedImage['textOverlay']) => void;

  setIsGenerating: (v: boolean) => void;
  setCurrentSessionId: (id: string | null) => void;
  addToGenerationHistory: () => void;
  restoreFromHistory: (item: GenerationHistoryItem) => void;
  clearGenerated: () => void;
  clearAll: () => void;
}

const initialPlatformTones: Record<Platform, ToneType | null> = {
  instagram: null,
  linkedin: null,
  facebook: null,
  twitter: null,
};

const initialPostIndex: Record<Platform, number> = {
  instagram: 0,
  linkedin: 0,
  facebook: 0,
  twitter: 0,
};

const initialImageRecs: Record<Platform, number> = {
  instagram: 0,
  linkedin: 0,
  facebook: 0,
  twitter: 0,
};

const initialCustomImages: Record<Platform, string | null> = {
  instagram: null,
  linkedin: null,
  facebook: null,
  twitter: null,
};

const initialAIImages: Record<Platform, AIGeneratedImage | null> = {
  instagram: null,
  linkedin: null,
  facebook: null,
  twitter: null,
};

const initialAICarouselData: Record<Platform, AICarouselData | null> = {
  instagram: null,
  linkedin: null,
  facebook: null,
  twitter: null,
};

const initialAIImageLoading: Record<Platform, boolean> = {
  instagram: false,
  linkedin: false,
  facebook: false,
  twitter: false,
};

const initialAIImageError: Record<Platform, string | null> = {
  instagram: null,
  linkedin: null,
  facebook: null,
  twitter: null,
};

// Frozen empty workspace data - stable reference to prevent infinite re-renders
// This is used when no workspace is selected or when workspace data doesn't exist
const EMPTY_WORKSPACE_DATA: WorkspacePostData = Object.freeze({
  prompt: '',
  selectedServices: ['general'],
  selectedPlatforms: [] as Platform[],
  platformTones: initialPlatformTones,
  generatedData: {} as Record<Platform, GeneratedPlatformData>,
  selectedPostIndex: initialPostIndex,
  sharedPrinciples: [] as string[],
  sharedExplanation: '',
  psychologySelection: null,
  savedPosts: {} as Record<string, SavedPostInfo>,
  unifiedImages: [] as UnifiedImage[],
  platformImageRecommendations: initialImageRecs,
  customImages: initialCustomImages,
  aiGeneratedImages: initialAIImages,
  aiCarouselData: initialAICarouselData,
  generationTiming: null,
  generationHistory: [] as GenerationHistoryItem[],
  currentSessionId: null,
  lastAccessedAt: 0,
}) as WorkspacePostData;

// Create empty workspace data with defaults (for new workspaces - creates mutable copy)
const getEmptyWorkspaceData = (): WorkspacePostData => ({
  prompt: '',
  selectedServices: ['general'],
  selectedPlatforms: [],
  platformTones: { ...initialPlatformTones },
  generatedData: {} as Record<Platform, GeneratedPlatformData>,
  selectedPostIndex: { ...initialPostIndex },
  sharedPrinciples: [],
  sharedExplanation: '',
  psychologySelection: null,
  savedPosts: {},
  unifiedImages: [],
  platformImageRecommendations: { ...initialImageRecs },
  customImages: { ...initialCustomImages },
  aiGeneratedImages: { ...initialAIImages },
  aiCarouselData: { ...initialAICarouselData },
  generationTiming: null,
  generationHistory: [],
  currentSessionId: null,
  lastAccessedAt: Date.now(),
});

// Max number of workspaces to keep in localStorage (LRU eviction)
const MAX_WORKSPACES_CACHED = 5;

// Evict oldest workspaces if over limit
const evictOldWorkspaces = (
  workspaceData: Record<string, WorkspacePostData>,
  maxCount: number
): Record<string, WorkspacePostData> => {
  const entries = Object.entries(workspaceData);
  if (entries.length <= maxCount) return workspaceData;

  // Sort by lastAccessedAt (oldest first)
  entries.sort((a, b) => a[1].lastAccessedAt - b[1].lastAccessedAt);

  // Keep only the most recent maxCount entries
  const toKeep = entries.slice(entries.length - maxCount);
  return Object.fromEntries(toKeep);
};

// Helper to get current workspace data from state
const getCurrentWorkspaceData = (state: { currentWorkspaceId: string | null; workspaceData: Record<string, WorkspacePostData> }): WorkspacePostData => {
  const wsId = state.currentWorkspaceId;
  if (!wsId) return getEmptyWorkspaceData();
  return state.workspaceData[wsId] || getEmptyWorkspaceData();
};

// Helper to update current workspace data
const updateCurrentWorkspaceData = (
  state: { currentWorkspaceId: string | null; workspaceData: Record<string, WorkspacePostData>; _cachedWorkspaceData: WorkspacePostData },
  updater: (data: WorkspacePostData) => Partial<WorkspacePostData>
) => {
  const wsId = state.currentWorkspaceId;
  if (!wsId) return {};

  const currentData = state.workspaceData[wsId] || getEmptyWorkspaceData();
  const updates = updater(currentData);

  const newWsData: WorkspacePostData = {
    ...currentData,
    ...updates,
    lastAccessedAt: Date.now(),
  };

  return {
    workspaceData: {
      ...state.workspaceData,
      [wsId]: newWsData,
    },
    _cachedWorkspaceData: newWsData,  // Update cache with new data
  };
};

// Create the store (no persistence - data clears on page refresh)
const usePostsStoreBase = create<PostsState>()(
  (set, get) => ({
    // Global state
    currentWorkspaceId: null,
    imageMode: 'none',
    carouselMode: 'none',
    wantsTextCarousel: false,
    threadMode: false,

    // Transient state
    isGenerating: false,
    aiImageLoading: { ...initialAIImageLoading },
    aiImageError: { ...initialAIImageError },

    // Workspace data
    workspaceData: {},

    // Cached workspace data - stable reference for selectors
    _cachedWorkspaceData: EMPTY_WORKSPACE_DATA,

      // Switch to a workspace (called when activeWorkspace changes)
      setCurrentWorkspace: (workspaceId: string | null) => {
        set((state) => {
          if (!workspaceId) {
            return {
              currentWorkspaceId: null,
              _cachedWorkspaceData: EMPTY_WORKSPACE_DATA,
            };
          }

          // Initialize workspace data if doesn't exist
          let wsData = state.workspaceData[workspaceId];
          if (!wsData) {
            wsData = getEmptyWorkspaceData();
          } else {
            // Update lastAccessedAt
            wsData = { ...wsData, lastAccessedAt: Date.now() };
          }

          // Evict old workspaces if > MAX_WORKSPACES_CACHED
          const newWorkspaceData = evictOldWorkspaces(
            { ...state.workspaceData, [workspaceId]: wsData },
            MAX_WORKSPACES_CACHED
          );

          return {
            currentWorkspaceId: workspaceId,
            workspaceData: newWorkspaceData,
            _cachedWorkspaceData: wsData,  // Update cache with current workspace data
            // Reset transient AI states when switching
            aiImageLoading: { ...initialAIImageLoading },
            aiImageError: { ...initialAIImageError },
          };
        });
      },

      // Actions - update current workspace data
      setPrompt: (prompt) => set((state) => updateCurrentWorkspaceData(state, () => ({ prompt }))),

      setSelectedServices: (selectedServices) => set((state) => updateCurrentWorkspaceData(state, () => ({ selectedServices }))),

      setImageMode: (imageMode) => set({
        imageMode,
        // Mutual exclusivity: activating an image mode disables carousel
        ...(imageMode !== 'none' ? { carouselMode: 'none' as CarouselMode } : {}),
      }),

      setCarouselMode: (carouselMode) => set({
        carouselMode,
        // Mutual exclusivity: activating a carousel mode disables image
        ...(carouselMode !== 'none' ? { imageMode: 'none' as ImageMode } : {}),
      }),

      setWantsTextCarousel: (wantsTextCarousel) => set({ wantsTextCarousel }),

      setThreadMode: (threadMode) => set({ threadMode }),

      setSelectedPlatforms: (selectedPlatforms) => set((state) => updateCurrentWorkspaceData(state, () => ({ selectedPlatforms }))),

      togglePlatform: (platform) => set((state) => {
        const currentData = getCurrentWorkspaceData(state);
        const isRemoving = currentData.selectedPlatforms.includes(platform);
        const platforms = isRemoving
          ? currentData.selectedPlatforms.filter((p) => p !== platform)
          : [...currentData.selectedPlatforms, platform];

        // If removing platform, clear its generated data and images
        if (isRemoving) {
          const newGeneratedData = { ...currentData.generatedData };
          delete newGeneratedData[platform];

          return updateCurrentWorkspaceData(state, () => ({
            selectedPlatforms: platforms,
            generatedData: newGeneratedData,
            aiGeneratedImages: { ...currentData.aiGeneratedImages, [platform]: null },
            customImages: { ...currentData.customImages, [platform]: null },
            selectedPostIndex: { ...currentData.selectedPostIndex, [platform]: 0 },
          }));
        }

        return updateCurrentWorkspaceData(state, () => ({ selectedPlatforms: platforms }));
      }),

      setPlatformTone: (platform, tone) => set((state) =>
        updateCurrentWorkspaceData(state, (data) => ({
          platformTones: { ...data.platformTones, [platform]: tone },
        }))
      ),

      setGeneratedData: (generatedData) => set((state) => updateCurrentWorkspaceData(state, () => ({ generatedData }))),

      updatePlatformData: (platform, data) => set((state) =>
        updateCurrentWorkspaceData(state, (currentData) => ({
          generatedData: { ...currentData.generatedData, [platform]: data },
        }))
      ),

      setSelectedPostIndex: (platform, index) => set((state) =>
        updateCurrentWorkspaceData(state, (data) => ({
          selectedPostIndex: { ...data.selectedPostIndex, [platform]: index },
        }))
      ),

      // Shared principles and explanation
      setSharedPrinciples: (sharedPrinciples) => set((state) => updateCurrentWorkspaceData(state, () => ({ sharedPrinciples }))),
      setSharedExplanation: (sharedExplanation) => set((state) => updateCurrentWorkspaceData(state, () => ({ sharedExplanation }))),
      setPsychologySelection: (psychologySelection) => set((state) => updateCurrentWorkspaceData(state, () => ({ psychologySelection }))),

      setGenerationTiming: (generationTiming) => set((state) => updateCurrentWorkspaceData(state, () => ({ generationTiming }))),

      // Saved posts management
      setSavedPost: (platform, variantIndex, dbId, isFavorited) => set((state) =>
        updateCurrentWorkspaceData(state, (data) => ({
          savedPosts: {
            ...data.savedPosts,
            [`${platform}-${variantIndex}`]: { dbId, isFavorited },
          },
        }))
      ),

      updateSavedPostFavorite: (platform, variantIndex, isFavorited) => set((state) => {
        const currentData = getCurrentWorkspaceData(state);
        const key = `${platform}-${variantIndex}`;
        const existing = currentData.savedPosts[key];
        if (!existing) return {};
        return updateCurrentWorkspaceData(state, (data) => ({
          savedPosts: {
            ...data.savedPosts,
            [key]: { ...existing, isFavorited },
          },
        }));
      }),

      getSavedPost: (platform, variantIndex) => {
        const state = get();
        const currentData = getCurrentWorkspaceData(state);
        return currentData.savedPosts[`${platform}-${variantIndex}`];
      },

      setUnifiedImages: (unifiedImages) => set((state) => updateCurrentWorkspaceData(state, () => ({ unifiedImages }))),

      setPlatformImageRecommendations: (platformImageRecommendations) => set((state) =>
        updateCurrentWorkspaceData(state, () => ({ platformImageRecommendations }))
      ),

      setCustomImage: (platform, image) => set((state) =>
        updateCurrentWorkspaceData(state, (data) => ({
          customImages: { ...data.customImages, [platform]: image },
        }))
      ),

      // AI Image actions
      setAIGeneratedImage: (platform, image) => set((state) =>
        updateCurrentWorkspaceData(state, (data) => ({
          aiGeneratedImages: { ...data.aiGeneratedImages, [platform]: image },
        }))
      ),

      setTextOverlay: (platform, overlay) => set((state) =>
        updateCurrentWorkspaceData(state, (data) => ({
          aiGeneratedImages: {
            ...data.aiGeneratedImages,
            [platform]: data.aiGeneratedImages[platform]
              ? { ...data.aiGeneratedImages[platform]!, textOverlay: overlay }
              : null,
          },
        }))
      ),

      setAIImageLoading: (platform, loading) => set((state) => ({
        aiImageLoading: { ...state.aiImageLoading, [platform]: loading },
      })),

      setAIImageError: (platform, error) => set((state) => ({
        aiImageError: { ...state.aiImageError, [platform]: error },
      })),

      clearAIImages: () => set((state) => ({
        ...updateCurrentWorkspaceData(state, () => ({
          aiGeneratedImages: { ...initialAIImages },
        })),
        aiImageLoading: { ...initialAIImageLoading },
        aiImageError: { ...initialAIImageError },
      })),

      // Carousel actions
      setAICarouselData: (platform, data) => set((state) =>
        updateCurrentWorkspaceData(state, (currentData) => ({
          aiCarouselData: { ...currentData.aiCarouselData, [platform]: data },
        }))
      ),

      updateCarouselSlide: (platform, slideIndex, slideUpdate) => set((state) =>
        updateCurrentWorkspaceData(state, (currentData) => {
          const carouselData = currentData.aiCarouselData[platform];
          if (!carouselData) return {};
          const newSlides = [...carouselData.slides];
          newSlides[slideIndex] = { ...newSlides[slideIndex], ...slideUpdate };
          return {
            aiCarouselData: {
              ...currentData.aiCarouselData,
              [platform]: { ...carouselData, slides: newSlides },
            },
          };
        })
      ),

      setCarouselActiveSlide: (platform, index) => set((state) =>
        updateCurrentWorkspaceData(state, (currentData) => {
          const carouselData = currentData.aiCarouselData[platform];
          if (!carouselData) return {};
          return {
            aiCarouselData: {
              ...currentData.aiCarouselData,
              [platform]: { ...carouselData, activeSlideIndex: index },
            },
          };
        })
      ),

      setCarouselSlideTextOverlay: (platform, slideIndex, overlay) => set((state) =>
        updateCurrentWorkspaceData(state, (currentData) => {
          const carouselData = currentData.aiCarouselData[platform];
          if (!carouselData) return {};
          const newSlides = [...carouselData.slides];
          newSlides[slideIndex] = { ...newSlides[slideIndex], textOverlay: overlay };
          return {
            aiCarouselData: {
              ...currentData.aiCarouselData,
              [platform]: { ...carouselData, slides: newSlides },
            },
          };
        })
      ),

      setIsGenerating: (v) => set({ isGenerating: v }),

      setCurrentSessionId: (id) => set((state) =>
        updateCurrentWorkspaceData(state, () => ({ currentSessionId: id }))
      ),

      addToGenerationHistory: () => set((state) => {
        const wsId = state.currentWorkspaceId;
        if (!wsId) return {};
        const currentData = state.workspaceData[wsId] || getEmptyWorkspaceData();
        if (Object.keys(currentData.generatedData).length === 0) return {};
        const item: GenerationHistoryItem = {
          id: Date.now().toString(),
          prompt: currentData.prompt,
          platforms: [...currentData.selectedPlatforms],
          generatedData: { ...currentData.generatedData },
          selectedPostIndex: { ...currentData.selectedPostIndex },
          unifiedImages: [...currentData.unifiedImages],
          aiGeneratedImages: { ...currentData.aiGeneratedImages },
          aiCarouselData: { ...currentData.aiCarouselData },
          sharedPrinciples: [...currentData.sharedPrinciples],
          sharedExplanation: currentData.sharedExplanation,
          psychologySelection: currentData.psychologySelection,
          timestamp: Date.now(),
        };
        const history = [item, ...(currentData.generationHistory || [])].slice(0, 5);
        return updateCurrentWorkspaceData(state, () => ({ generationHistory: history }));
      }),

      restoreFromHistory: (item) => set((state) =>
        updateCurrentWorkspaceData(state, () => ({
          prompt: item.prompt,
          selectedPlatforms: item.platforms,
          generatedData: item.generatedData,
          selectedPostIndex: item.selectedPostIndex,
          unifiedImages: item.unifiedImages,
          aiGeneratedImages: Object.fromEntries(
            Object.entries(item.aiGeneratedImages).map(([k, v]) => [k, v ? { ...v, edit_count: 0 } : v])
          ) as typeof item.aiGeneratedImages,
          aiCarouselData: item.aiCarouselData || { ...initialAICarouselData },
          sharedPrinciples: item.sharedPrinciples,
          sharedExplanation: item.sharedExplanation,
          psychologySelection: item.psychologySelection ?? null,
        }))
      ),

      clearGenerated: () => set((state) => {
        const result = updateCurrentWorkspaceData(state, () => ({
          generatedData: {} as Record<Platform, GeneratedPlatformData>,
          selectedPostIndex: { ...initialPostIndex },
          sharedPrinciples: [],
          sharedExplanation: '',
          psychologySelection: null,
          savedPosts: {},
          unifiedImages: [],
          platformImageRecommendations: { ...initialImageRecs },
          customImages: { ...initialCustomImages },
          aiGeneratedImages: { ...initialAIImages },
          aiCarouselData: { ...initialAICarouselData },
          generationTiming: null,
          currentSessionId: null,
        }));
        return {
          ...result,
          aiImageLoading: { ...initialAIImageLoading },
          aiImageError: { ...initialAIImageError },
        };
      }),

      clearAll: () => set({
        // Clear ALL workspace data (used on logout to prevent data leakage)
        currentWorkspaceId: null,
        workspaceData: {},
        _cachedWorkspaceData: EMPTY_WORKSPACE_DATA,  // Reset cache to stable empty reference
        imageMode: 'none',
        carouselMode: 'none',
        wantsTextCarousel: false,
        threadMode: false,
        isGenerating: false,
        aiImageLoading: { ...initialAIImageLoading },
        aiImageError: { ...initialAIImageError },
      }),
    })
);

// Custom hook that provides computed properties for current workspace data
// Uses _cachedWorkspaceData for stable references to prevent infinite re-renders
export const usePostsStore = <T>(selector: (state: PostsState & {
  prompt: string;
  selectedServices: string[];
  selectedPlatforms: Platform[];
  platformTones: Record<Platform, ToneType | null>;
  generatedData: Record<Platform, GeneratedPlatformData>;
  selectedPostIndex: Record<Platform, number>;
  sharedPrinciples: string[];
  sharedExplanation: string;
  psychologySelection: import('@/types').PrincipleSelectionEvent | null;
  savedPosts: Record<string, SavedPostInfo>;
  unifiedImages: UnifiedImage[];
  platformImageRecommendations: Record<Platform, number>;
  customImages: Record<Platform, string | null>;
  aiGeneratedImages: Record<Platform, AIGeneratedImage | null>;
  aiCarouselData: Record<Platform, AICarouselData | null>;
  generationTiming: GenerationTiming | null;
  generationHistory: GenerationHistoryItem[];
  currentSessionId: string | null;
}) => T): T => {
  return usePostsStoreBase((state) => {
    // Use cached workspace data for stable references
    // This prevents creating new object references on every selector call
    const wsData = state._cachedWorkspaceData;
    // Merge workspace-specific data as direct properties for backward compatibility
    const enhancedState = {
      ...state,
      prompt: wsData.prompt,
      selectedServices: wsData.selectedServices,
      selectedPlatforms: wsData.selectedPlatforms,
      platformTones: wsData.platformTones,
      generatedData: wsData.generatedData,
      selectedPostIndex: wsData.selectedPostIndex,
      sharedPrinciples: wsData.sharedPrinciples,
      sharedExplanation: wsData.sharedExplanation,
      psychologySelection: wsData.psychologySelection ?? null,
      savedPosts: wsData.savedPosts,
      unifiedImages: wsData.unifiedImages,
      platformImageRecommendations: wsData.platformImageRecommendations,
      customImages: wsData.customImages,
      aiGeneratedImages: wsData.aiGeneratedImages,
      aiCarouselData: wsData.aiCarouselData || initialAICarouselData,
      generationTiming: wsData.generationTiming,
      generationHistory: wsData.generationHistory || [],
      currentSessionId: wsData.currentSessionId || null,
    };
    return selector(enhancedState);
  });
};

// Export getState for non-hook access (e.g., in callbacks)
// Uses _cachedWorkspaceData for consistency with the hook
usePostsStore.getState = () => {
  const state = usePostsStoreBase.getState();
  const wsData = state._cachedWorkspaceData;
  return {
    ...state,
    prompt: wsData.prompt,
    selectedServices: wsData.selectedServices,
    selectedPlatforms: wsData.selectedPlatforms,
    platformTones: wsData.platformTones,
    generatedData: wsData.generatedData,
    selectedPostIndex: wsData.selectedPostIndex,
    sharedPrinciples: wsData.sharedPrinciples,
    sharedExplanation: wsData.sharedExplanation,
    psychologySelection: wsData.psychologySelection ?? null,
    savedPosts: wsData.savedPosts,
    unifiedImages: wsData.unifiedImages,
    platformImageRecommendations: wsData.platformImageRecommendations,
    customImages: wsData.customImages,
    aiGeneratedImages: wsData.aiGeneratedImages,
    aiCarouselData: wsData.aiCarouselData || initialAICarouselData,
    generationTiming: wsData.generationTiming,
    generationHistory: wsData.generationHistory || [],
    currentSessionId: wsData.currentSessionId || null,
  };
};

// Subscribe method for external listeners
usePostsStore.subscribe = usePostsStoreBase.subscribe;
